import { Amplify } from 'aws-amplify';
import {
  confirmSignIn,
  fetchAuthSession,
  fetchMFAPreference,
  getCurrentUser,
  signIn,
  setUpTOTP,
  signOut,
  updateMFAPreference,
  verifyTOTPSetup,
  type SignInOutput,
} from 'aws-amplify/auth';

import { env } from '../env.ts';

// ADR-0006 rule 2: password plus TOTP, both the provider's, reached through choice-based
// authentication (`USER_AUTH` with `PASSWORD_SRP` preferred) — so the password is proved to Cognito
// rather than sent to it, and the client-based password flows are not enabled on the app client at
// all. Amazon's library owns every part of that exchange; ADR-0006 was chosen precisely so that no
// security-sensitive code is written here, and this module is the thin edge of it.
//
// AVenue renders its own screens (ADR-0004 rule 5, ADO-01, INV-13) — the Cognito hosted UI is
// never used, which is why the challenge sequence is handled explicitly below.

Amplify.configure({
  Auth: {
    Cognito: {
      userPoolId: env.userPoolId,
      userPoolClientId: env.userPoolClientId,
      // No identity pool: AVenue's API is the only thing a token is presented to, and AWS
      // resources are never reached from the browser.
    },
  },
});

/**
 * Where a sign-in has got to. Cognito decides the sequence; these are the states the office path
 * can reach. Anything else is surfaced as an error rather than silently treated as success — a
 * sign-in step nobody has implemented must not become a session.
 */
export type SignInState =
  | { readonly step: 'done' }
  /** This account has a password: ask for it (ADR-0006 rule 2, above the price line). */
  | { readonly step: 'password' }
  /** No password: Cognito has emailed a one-time code (CB-REQ ADO-02, crew part 2). */
  | { readonly step: 'email-code' }
  | { readonly step: 'new-password-required' }
  | { readonly step: 'totp-setup'; readonly setupUri: string; readonly secret: string }
  | { readonly step: 'totp-code' };

export class AuthError extends Error {
  constructor(
    message: string,
    override readonly cause?: unknown,
  ) {
    super(message);
    this.name = 'AuthError';
  }
}

/**
 * The first step: an address and nothing else. Cognito's choice-based sign-in answers with the
 * factors that account can use. **An account with a password always goes the password way**, so the
 * office never lands on the passcode path from this screen (ADR-0006); an account with none — crew,
 * created by ADR-0013 — is sent a code at once. The screen is not the enforcement of either.
 */
/** ADR-0015: the pool's usernames are case-sensitive, and every identity is made lower case. */
const canonical = (email: string) => email.trim().toLowerCase();

export async function beginSignIn(email: string): Promise<SignInState> {
  try {
    const output = await signIn({
      username: canonical(email),
      options: { authFlowType: 'USER_AUTH' },
    });
    const next = output.nextStep;
    if (next.signInStep === 'CONTINUE_SIGN_IN_WITH_FIRST_FACTOR_SELECTION') {
      const offered = next.availableChallenges ?? [];
      if (offered.includes('PASSWORD_SRP') || offered.includes('PASSWORD')) {
        return { step: 'password' };
      }
      if (offered.includes('EMAIL_OTP')) {
        return toState(await confirmSignIn({ challengeResponse: 'EMAIL_OTP' }));
      }
    }
    return toState(output);
  } catch (error) {
    throw new AuthError(signInMessage(error), error);
  }
}

/** The password step for an account that has one. The returned state says which screen is next. */
export async function startSignIn(email: string, password: string): Promise<SignInState> {
  try {
    const output = await signIn({
      username: canonical(email),
      password,
      options: { authFlowType: 'USER_AUTH', preferredChallenge: 'PASSWORD_SRP' },
    });
    return toState(output);
  } catch (error) {
    throw new AuthError(signInMessage(error), error);
  }
}

/** Answers whatever challenge the previous step asked for. */
export async function answerChallenge(response: string): Promise<SignInState> {
  try {
    return toState(await confirmSignIn({ challengeResponse: response }));
  } catch (error) {
    throw new AuthError(signInMessage(error), error);
  }
}

/**
 * Starts TOTP enrolment for a user who is already signed in — the case Cognito never prompts for,
 * because pool MFA is `OPTIONAL` and it therefore neither requires the factor nor offers setup.
 *
 * Returns what the person needs to add the account: a URI their authenticator app can open, and the
 * same secret in text for when a camera is not available.
 */
export async function beginTotpEnrolment(): Promise<{ uri: string; secret: string }> {
  try {
    // Every call generates a new secret and supersedes the last one Cognito was waiting on, so this
    // must be called once per attempt and never speculatively. It is a mutation, not a fetch.
    const details = await setUpTOTP();
    const who = await currentAccountLabel();
    return {
      // Issuer and account, so an authenticator app lists this as "AVenue (someone@…)" rather
      // than one anonymous "AVenue" entry among however many the person already has.
      uri: details.getSetupUri('AVenue', who).toString(),
      secret: details.sharedSecret,
    };
  } catch (error) {
    throw new AuthError('Could not start authenticator setup. Try reloading the page.', error);
  }
}

/** The signed-in user's address, for labelling the authenticator entry. Never load-bearing. */
async function currentAccountLabel(): Promise<string> {
  try {
    const user = await getCurrentUser();
    return user.signInDetails?.loginId ?? user.username;
  } catch {
    return 'account';
  }
}

/**
 * Completes TOTP enrolment and makes it the preferred factor.
 *
 * Pool MFA is `OPTIONAL` — the only setting Cognito accepts alongside `EMAIL_OTP` (ADR-0006 rule 3)
 * — so enrolment is not forced by the pool and this is what puts the factor in place.
 *
 * The **enforcement** is the API's, in the guard, on every request: this screen is how a person
 * satisfies it, not what makes it required. CB-CONV §3.8 is explicit that a UI guard is never the
 * enforcement, and deleting this file would lock the account out rather than let it in.
 */
export async function completeTotpEnrolment(code: string): Promise<void> {
  try {
    await verifyTOTPSetup({ code });
    // Enrolled is not enough. Cognito will leave a factor configured but not preferred, and the API
    // asks for PreferredMfaSetting — an unpreferred factor is never challenged for.
    await updateMFAPreference({ totp: 'PREFERRED' });
  } catch (error) {
    throw new AuthError(
      'That code was not accepted. Enter the code showing right now — they change every 30 seconds. ' +
        'If it keeps failing, check your device clock is set automatically, then start again below.',
      error,
    );
  }
}

/** Whether this user has TOTP enrolled and preferred, which is what ADR-0006 rule 3 will require. */
export async function hasPreferredTotp(): Promise<boolean> {
  const preference = await fetchMFAPreference();
  return preference.preferred === 'TOTP';
}

/**
 * The access token for the API, or null when nobody is signed in. Amplify refreshes it from the
 * refresh token when it has expired, which is what makes ADO-02's long sessions work without the
 * client holding anything itself.
 */
export async function accessToken(): Promise<string | null> {
  try {
    const session = await fetchAuthSession();
    return session.tokens?.accessToken?.toString() ?? null;
  } catch {
    return null;
  }
}

export async function endSession(): Promise<void> {
  await signOut();
}

function toState(output: SignInOutput): SignInState {
  const next = output.nextStep;
  switch (next.signInStep) {
    case 'DONE':
      return { step: 'done' };
    case 'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED':
      return { step: 'new-password-required' };
    case 'CONFIRM_SIGN_IN_WITH_TOTP_CODE':
      return { step: 'totp-code' };
    case 'CONFIRM_SIGN_IN_WITH_EMAIL_CODE':
      return { step: 'email-code' };
    case 'CONTINUE_SIGN_IN_WITH_TOTP_SETUP': {
      const details = next.totpSetupDetails;
      return {
        step: 'totp-setup',
        // The URI an authenticator app scans; the secret is shown as well, because a phone camera
        // is not always available and typing the secret is the documented fallback.
        setupUri: details.getSetupUri('AVenue').toString(),
        secret: details.sharedSecret,
      };
    }
    default:
      // SMS, passkeys, MFA selection: real Cognito steps, none of them implemented. Failing here is the point — a step that is not handled
      // must not fall through to a session.
      throw new AuthError(
        `This account needs a sign-in step AVenue does not support yet (${next.signInStep}).`,
      );
  }
}

function signInMessage(error: unknown): string {
  const name = (error as { name?: string } | null)?.name;
  switch (name) {
    case 'NotAuthorizedException':
      // Deliberately the same answer for a wrong password and an unknown address: the app client
      // has prevent_user_existence_errors enabled, and the screen must not undo that.
      return 'That email address and password did not match.';
    case 'UserNotFoundException':
      return 'That email address and password did not match.';
    case 'PasswordResetRequiredException':
      return 'This account needs its password reset. Ask the office to send a reset.';
    case 'TooManyRequestsException':
    case 'LimitExceededException':
      return 'Too many attempts. Wait a minute and try again.';
    case 'InvalidPasswordException':
      return 'That password does not meet the policy: at least 12 characters.';
    case 'CodeMismatchException':
      return 'That code was not accepted. Check it and try again.';
    case 'ExpiredCodeException':
      return 'That code has expired. Ask for a new one.';
    default:
      return 'Sign-in could not be completed. Try again, and tell the office if it keeps failing.';
  }
}
